import { useContext, createContext, useMemo, createElement, useCallback } from 'react';
import equals from 'fast-deep-equal';
import { useIdentifier } from '@stackup/form';

function _extends() {
  _extends = Object.assign || function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];

      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }

    return target;
  };

  return _extends.apply(this, arguments);
}

function _objectWithoutPropertiesLoose(source, excluded) {
  if (source == null) return {};
  var target = {};
  var sourceKeys = Object.keys(source);
  var key, i;

  for (i = 0; i < sourceKeys.length; i++) {
    key = sourceKeys[i];
    if (excluded.indexOf(key) >= 0) continue;
    target[key] = source[key];
  }

  return target;
}

function _unsupportedIterableToArray(o, minLen) {
  if (!o) return;
  if (typeof o === "string") return _arrayLikeToArray(o, minLen);
  var n = Object.prototype.toString.call(o).slice(8, -1);
  if (n === "Object" && o.constructor) n = o.constructor.name;
  if (n === "Map" || n === "Set") return Array.from(o);
  if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _arrayLikeToArray(o, minLen);
}

function _arrayLikeToArray(arr, len) {
  if (len == null || len > arr.length) len = arr.length;

  for (var i = 0, arr2 = new Array(len); i < len; i++) arr2[i] = arr[i];

  return arr2;
}

function _createForOfIteratorHelperLoose(o, allowArrayLike) {
  var it;

  if (typeof Symbol === "undefined" || o[Symbol.iterator] == null) {
    if (Array.isArray(o) || (it = _unsupportedIterableToArray(o)) || allowArrayLike && o && typeof o.length === "number") {
      if (it) o = it;
      var i = 0;
      return function () {
        if (i >= o.length) return {
          done: true
        };
        return {
          done: false,
          value: o[i++]
        };
      };
    }

    throw new TypeError("Invalid attempt to iterate non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method.");
  }

  it = o[Symbol.iterator]();
  return it.next.bind(it);
}

var noop = function noop() {
  return undefined;
};
/**
 * Concatenate conditional class names.
 *
 * ### Example
 * concat("input", touched && error && "input--error")
 */


function concat() {
  var out = "";

  for (var _len = arguments.length, names = new Array(_len), _key = 0; _key < _len; _key++) {
    names[_key] = arguments[_key];
  }

  for (var i = 0; i < names.length; i++) {
    if (names[i]) out += (out && " ") + names[i];
  }

  return out || undefined;
}
/**
 * Create a new theme.
 *
 * #### Example
 * const theme = createTheme({
 *   input: props => concat(
 *     "input",
 *     props.touched && "input--touched",
 *     props.touched && props.error && "input--error",
 *     ...props.types.map(type => `input--${type}`),
 *     ...props.variants.map(variant => variant && `input--${variant}`),
 *   )
 * });
 */

var createTheme = function createTheme(theme) {
  return _extends({
    fieldset: noop,
    legend: noop,
    field: noop,
    label: noop,
    input: noop,
    error: noop,
    help: noop
  }, theme);
};
var ThemeContext = /*#__PURE__*/createContext( /*#__PURE__*/createTheme({}));
ThemeContext.displayName = "Theme";
var useTheme = function useTheme() {
  return useContext(ThemeContext);
};
/**
 * Render a `ThemeProvider` at the top of your component tree
 * to provide a global theme.
 *
 * See `createTheme` for an example of how to define your theme.
 *
 * #### Example
 *
 * ```jsx
 * <ThemeProvider value={theme}>
 *   <App />
 * </ThemeProvider>
 * ```
 */

var ThemeProvider = ThemeContext.Provider;

function isString(value) {
  return typeof value === "string";
}
function isNumber(value) {
  return typeof value === "number";
}
function getDOMValue(value) {
  return isString(value) || isNumber(value) ? value : undefined;
}
function remove(values, value) {
  return values.filter(function (v) {
    return !equals(v, value);
  });
}
function contains(values, value) {
  for (var _iterator = _createForOfIteratorHelperLoose(values), _step; !(_step = _iterator()).done;) {
    var v = _step.value;
    if (equals(v, value)) return true;
  }

  return false;
}
/**
 * In the case of radios and checkboxes, we needed to distinguish each item from
 * other items. Howerver, many utilities rely on the field's ID property.
 *
 * To make sure that these items have unique IDs, we'll merge the new ID into the
 * field.
 */

function useNestedId(field) {
  var suffix = useIdentifier();
  return useMemo(function () {
    return _extends(_extends({}, field), {}, {
      id: field.id + "_" + suffix
    });
  }, [field, suffix]);
}

function getRelatedId(id, suffix) {
  return id + "--" + suffix;
}
function getError(field) {
  return field.touched && isString(field.error) ? field.error : undefined;
}
function useBlur(_ref) {
  var setTouched = _ref.setTouched;
  return useCallback(function () {
    return setTouched(true);
  }, [setTouched]);
}
function useFieldProps(props, types) {
  var innerRef = props.innerRef,
      field = props.field,
      variant = props.variant,
      label = props.label,
      help = props.help,
      append = props.append,
      prepend = props.prepend,
      _props$render = props.render,
      render = _props$render === void 0 ? createElement : _props$render,
      inputProps = _objectWithoutPropertiesLoose(props, ["innerRef", "field", "variant", "label", "help", "append", "prepend", "render", "className", "inputClassName", "labelClassName", "helpClassName", "errorClassName"]);

  var theme = useTheme();
  var onBlur = useBlur(field);
  var error = getError(field);
  var errorId = getRelatedId(field.id, "error");
  var themeProps = {
    value: field.value,
    error: field.error,
    touched: field.touched,
    types: types,
    variants: Array.isArray(variant) ? variant : [variant],
    prepend: Boolean(prepend),
    append: Boolean(append)
  };
  return _extends(_extends({}, inputProps), {}, {
    field: field,
    error: error,
    label: label,
    help: help,
    append: append,
    prepend: prepend,
    render: render,
    onBlur: onBlur,
    id: field.id,
    ref: innerRef,
    "aria-describedby": error ? errorId : undefined,
    className: concat(theme.input(themeProps), props.inputClassName),
    getFieldProps: function getFieldProps() {
      return {
        className: concat(theme.field(themeProps), props.className)
      };
    },
    getLabelProps: function getLabelProps() {
      return {
        htmlFor: field.id,
        className: concat(theme.label(themeProps), props.labelClassName)
      };
    },
    getErrorProps: function getErrorProps() {
      return {
        id: field.id + "--error",
        role: "alert",
        className: concat(theme.error(themeProps), props.errorClassName)
      };
    },
    getHelpProps: function getHelpProps() {
      return {
        className: concat(theme.help(themeProps), props.helpClassName)
      };
    }
  });
}

/**
 * Manages the state of a boolean value using a checkbox.
 *
 * In addition to the props listed below, this component accepts
 * all props for an HTML input.
 *
 * #### Example
 *
 * ```jsx
 * <Checkbox
 *   label="I agree"
 *   help="Did you read all 7,000 lines?"
 *   field={useField(form, "confirmation")}
 * />
 * ```
 */

function Checkbox(props) {
  var _useFieldProps = useFieldProps(props, ["check", "checkbox"]),
      field = _useFieldProps.field,
      label = _useFieldProps.label,
      help = _useFieldProps.help,
      error = _useFieldProps.error,
      append = _useFieldProps.append,
      prepend = _useFieldProps.prepend,
      render = _useFieldProps.render,
      getFieldProps = _useFieldProps.getFieldProps,
      getLabelProps = _useFieldProps.getLabelProps,
      getErrorProps = _useFieldProps.getErrorProps,
      getHelpProps = _useFieldProps.getHelpProps,
      inputProps = _objectWithoutPropertiesLoose(_useFieldProps, ["field", "label", "help", "error", "append", "prepend", "render", "getFieldProps", "getLabelProps", "getErrorProps", "getHelpProps"]);

  var value = field.value,
      setValue = field.setValue;
  var onChange = useCallback(function (event) {
    setValue(event.target.checked);
  }, [setValue]);
  return createElement("div", Object.assign({}, getFieldProps()), prepend, render("input", _extends(_extends({}, inputProps), {}, {
    type: "checkbox",
    checked: value,
    onChange: onChange
  })), append, createElement("label", Object.assign({}, getLabelProps()), label, help && createElement("span", Object.assign({}, getHelpProps()), help)), error && createElement("span", Object.assign({}, getErrorProps()), error));
}

/**
 * Manages an array of values, represented by checkboxes.
 *
 * In addition to the props listed below, this component accepts
 * all props for an HTML input.
 *
 * Note that this component will not render error messages. For that, you'll
 * want to wrap your checkboxes in a FieldSet.
 *
 * #### Example
 *
 * ```jsx
 * <FieldSet legend="Sport" field={useField(form, "sport")}>
 *   <CheckboxItem
 *     label="Soccer"
 *     value={{ name: "Soccer" }}
 *     field={useField(form, "sport")}
 *   />
 *   <CheckboxItem
 *     label="Baseball"
 *     value={{ name: "Baseball" }}
 *     field={useField(form, "sport")}
 *   />
 * </FieldSet>
 * ```
 */

function CheckboxItem(props) {
  var nested = _extends(_extends({}, props), {}, {
    field: useNestedId(props.field)
  });

  var _useFieldProps = useFieldProps(nested, ["check", "checkbox"]),
      field = _useFieldProps.field,
      label = _useFieldProps.label,
      help = _useFieldProps.help,
      append = _useFieldProps.append,
      prepend = _useFieldProps.prepend,
      render = _useFieldProps.render,
      item = _useFieldProps.value,
      getFieldProps = _useFieldProps.getFieldProps,
      getLabelProps = _useFieldProps.getLabelProps,
      getHelpProps = _useFieldProps.getHelpProps,
      inputProps = _objectWithoutPropertiesLoose(_useFieldProps, ["field", "label", "help", "error", "append", "prepend", "render", "value", "getFieldProps", "getLabelProps", "getErrorProps", "getHelpProps"]);

  var value = field.value,
      setValue = field.setValue;
  var onChange = useCallback(function (event) {
    var checked = event.target.checked;
    setValue(function (value) {
      return checked ? [].concat(value, [item]) : remove(value, item);
    });
  }, [item, setValue]);
  return createElement("div", Object.assign({}, getFieldProps()), prepend, render("input", _extends(_extends({}, inputProps), {}, {
    onChange: onChange,
    type: "checkbox",
    value: getDOMValue(item),
    checked: contains(value, item)
  })), append, createElement("label", Object.assign({}, getLabelProps()), label, help && createElement("span", Object.assign({}, getHelpProps()), help)));
}

/**
 * Fields that are composed of multiple components (e.g. Radio and CheckboxItem)
 * should always be rendered within a fieldset.
 *
 * If an error is associated with the field, it will be rendered within the
 * fieldset, rather than on a Radio or CheckboxItem.
 *
 * #### Example
 *
 * ```jsx
 * <FieldSet legend="Sport" field={useField(form, "sport")}>
 *   <Radio
 *     label="Soccer"
 *     value={{ name: "Soccer" }}
 *     field={useField(form, "sport")}
 *   />
 *   <Radio
 *     label="Baseball"
 *     value={{ name: "Baseball" }}
 *     field={useField(form, "sport")}
 *   />
 * </FieldSet>
 * ```
 */

function FieldSet(props) {
  var field = props.field,
      legend = props.legend,
      help = props.help,
      children = props.children;
  var error = getError(field);
  var errorId = getRelatedId(field.id, "error");
  var describedBy = error ? errorId : undefined;
  var theme = useTheme();
  var themeProps = {
    value: field.value,
    error: field.error,
    touched: field.touched,
    types: ["fieldset"],
    variants: Array.isArray(props.variant) ? props.variant : [props.variant],
    prepend: false,
    append: false
  };
  return createElement("fieldset", {
    className: concat(theme.fieldset(themeProps), props.className),
    "aria-describedby": describedBy
  }, createElement("legend", {
    className: concat(theme.legend(themeProps), props.legendClassName)
  }, legend), help && createElement("p", {
    className: concat(theme.help(themeProps), props.helpClassName)
  }, help), children, error && createElement("span", {
    id: errorId,
    role: "alert",
    className: concat(theme.error(themeProps), props.errorClassName)
  }, error));
}

/**
 * Renders an `<input type="file" />`.
 *
 * In addition to the props listed below, this component accepts
 * all props for an HTML input.
 *
 * #### Example
 *
 * ```jsx
 * <FileInput label="Image" field={useField(form, "image")} />
 * ```
 */

function FileInput(props) {
  var _useFieldProps = useFieldProps(props, ["input", "file"]),
      field = _useFieldProps.field,
      label = _useFieldProps.label,
      help = _useFieldProps.help,
      error = _useFieldProps.error,
      append = _useFieldProps.append,
      prepend = _useFieldProps.prepend,
      render = _useFieldProps.render,
      getFieldProps = _useFieldProps.getFieldProps,
      getLabelProps = _useFieldProps.getLabelProps,
      getErrorProps = _useFieldProps.getErrorProps,
      getHelpProps = _useFieldProps.getHelpProps,
      inputProps = _objectWithoutPropertiesLoose(_useFieldProps, ["field", "label", "help", "error", "append", "prepend", "render", "getFieldProps", "getLabelProps", "getErrorProps", "getHelpProps"]);

  var setValue = field.setValue;
  var onChange = useCallback(function (event) {
    var _event$target$files;

    setValue(((_event$target$files = event.target.files) === null || _event$target$files === void 0 ? void 0 : _event$target$files[0]) || null);
  }, [setValue]);
  return createElement("div", Object.assign({}, getFieldProps()), createElement("label", Object.assign({}, getLabelProps()), label, help && createElement("span", Object.assign({}, getHelpProps()), help)), prepend, render("input", _extends(_extends({}, inputProps), {}, {
    type: "file",
    onChange: onChange
  })), append, error && createElement("span", Object.assign({}, getErrorProps()), error));
}

/**
 * Renders an `<input type="file" multiple />`.
 *
 * In addition to the props listed below, this component accepts
 * all props for an HTML input.
 *
 * #### Example
 *
 * ```jsx
 * <FileListInput label="Images" field={useField(form, "images")} />
 * ```
 */

function FileListInput(props) {
  var _useFieldProps = useFieldProps(props, ["input", "file"]),
      field = _useFieldProps.field,
      label = _useFieldProps.label,
      help = _useFieldProps.help,
      error = _useFieldProps.error,
      append = _useFieldProps.append,
      prepend = _useFieldProps.prepend,
      render = _useFieldProps.render,
      getFieldProps = _useFieldProps.getFieldProps,
      getLabelProps = _useFieldProps.getLabelProps,
      getErrorProps = _useFieldProps.getErrorProps,
      getHelpProps = _useFieldProps.getHelpProps,
      inputProps = _objectWithoutPropertiesLoose(_useFieldProps, ["field", "label", "help", "error", "append", "prepend", "render", "getFieldProps", "getLabelProps", "getErrorProps", "getHelpProps"]);

  var setValue = field.setValue;
  var onChange = useCallback(function (event) {
    setValue(event.target.files);
  }, [setValue]);
  return createElement("div", Object.assign({}, getFieldProps()), createElement("label", Object.assign({}, getLabelProps()), label, help && createElement("span", Object.assign({}, getHelpProps()), help)), prepend, render("input", _extends(_extends({}, inputProps), {}, {
    type: "file",
    multiple: true,
    onChange: onChange
  })), append, error && createElement("span", Object.assign({}, getErrorProps()), error));
}

/**
 * Renders an `<input />`, which whatever type you provide.
 *
 * The value of this input will always be a string, but you should use a
 * validation library such as [@stackup/validate](https://github.com/rzane/validate)
 * to parse and validate the entry before submission.
 *
 * In addition to the props listed below, this component accepts
 * all props for an HTML input.
 *
 * #### Example
 *
 * ```jsx
 * <Input
 *   type="email"
 *   label="Email"
 *   field={useField(form, "email")}
 * />
 * ```
 */

function Input(props) {
  var _props$type = props.type,
      type = _props$type === void 0 ? "text" : _props$type;

  var _useFieldProps = useFieldProps(props, ["input", type]),
      field = _useFieldProps.field,
      label = _useFieldProps.label,
      help = _useFieldProps.help,
      error = _useFieldProps.error,
      append = _useFieldProps.append,
      prepend = _useFieldProps.prepend,
      render = _useFieldProps.render,
      getFieldProps = _useFieldProps.getFieldProps,
      getLabelProps = _useFieldProps.getLabelProps,
      getErrorProps = _useFieldProps.getErrorProps,
      getHelpProps = _useFieldProps.getHelpProps,
      inputProps = _objectWithoutPropertiesLoose(_useFieldProps, ["field", "label", "help", "error", "append", "prepend", "render", "getFieldProps", "getLabelProps", "getErrorProps", "getHelpProps"]);

  var value = field.value,
      setValue = field.setValue;
  var onChange = useCallback(function (event) {
    setValue(event.target.value);
  }, [setValue]);
  return createElement("div", Object.assign({}, getFieldProps()), createElement("label", Object.assign({}, getLabelProps()), label, help && createElement("span", Object.assign({}, getHelpProps()), help)), prepend, render("input", _extends(_extends({}, inputProps), {}, {
    value: value,
    type: type,
    onChange: onChange
  })), append, error && createElement("span", Object.assign({}, getErrorProps()), error));
}

/**
 * Renders an `<input type="radio" />` and manages the state of any type of value.
 *
 * Note that this component will not render error messages. For that, you'll
 * want to wrap your radio options in a FieldSet.
 *
 * In addition to the props listed below, this component accepts
 * all props for an HTML input.
 *
 * #### Example
 *
 * ```jsx
 * <FieldSet legend="Sport" field={useField(form, "sport")}>
 *   <Radio
 *     label="Soccer"
 *     value={{ name: "Soccer" }}
 *     field={useField(form, "sport")}
 *   />
 *   <Radio
 *     label="Baseball"
 *     value={{ name: "Baseball" }}
 *     field={useField(form, "sport")}
 *   />
 * </FieldSet>
 * ```
 */

function Radio(props) {
  var nested = _extends(_extends({}, props), {}, {
    field: useNestedId(props.field)
  });

  var _useFieldProps = useFieldProps(nested, ["check", "radio"]),
      field = _useFieldProps.field,
      label = _useFieldProps.label,
      help = _useFieldProps.help,
      append = _useFieldProps.append,
      prepend = _useFieldProps.prepend,
      render = _useFieldProps.render,
      item = _useFieldProps.value,
      getFieldProps = _useFieldProps.getFieldProps,
      getLabelProps = _useFieldProps.getLabelProps,
      getHelpProps = _useFieldProps.getHelpProps,
      inputProps = _objectWithoutPropertiesLoose(_useFieldProps, ["field", "label", "help", "error", "append", "prepend", "render", "value", "getFieldProps", "getLabelProps", "getErrorProps", "getHelpProps"]);

  var value = field.value,
      setValue = field.setValue;
  var onChange = useCallback(function () {
    return setValue(item);
  }, [item, setValue]);
  return createElement("div", Object.assign({}, getFieldProps()), prepend, render("input", _extends(_extends({}, inputProps), {}, {
    onChange: onChange,
    type: "radio",
    value: getDOMValue(props.value),
    checked: equals(value, props.value)
  })), append, createElement("label", Object.assign({}, getLabelProps()), label, help && createElement("span", Object.assign({}, getHelpProps()), help)));
}

/**
 * Renders a `<select />` tag with whatever options you provide.
 *
 * You can provide a placeholder, which will render a disabled option
 * with a blank value.
 *
 * In addition to the props listed below, this component accepts
 * all props for an HTML select.
 *
 * #### Example
 *
 * ```jsx
 * <Select
 *   label="Sport"
 *   placeholder="Choose a sport"
 *   field={useField(form, "sport")}
 * >
 *   <option value="baseball">Baseball</option>
 *   <option value="soccer">Soccer</option>
 * </Select>
 * ```
 */

function Select(props) {
  var _useFieldProps = useFieldProps(props, ["select"]),
      field = _useFieldProps.field,
      label = _useFieldProps.label,
      help = _useFieldProps.help,
      error = _useFieldProps.error,
      append = _useFieldProps.append,
      prepend = _useFieldProps.prepend,
      render = _useFieldProps.render,
      placeholder = _useFieldProps.placeholder,
      children = _useFieldProps.children,
      getFieldProps = _useFieldProps.getFieldProps,
      getLabelProps = _useFieldProps.getLabelProps,
      getErrorProps = _useFieldProps.getErrorProps,
      getHelpProps = _useFieldProps.getHelpProps,
      inputProps = _objectWithoutPropertiesLoose(_useFieldProps, ["field", "label", "help", "error", "append", "prepend", "render", "placeholder", "children", "getFieldProps", "getLabelProps", "getErrorProps", "getHelpProps"]);

  var value = field.value,
      setValue = field.setValue;
  var htmlValue = value === null ? "" : value;
  var onChange = useCallback(function (event) {
    setValue(event.target.value || null);
  }, [setValue]);
  return createElement("div", Object.assign({}, getFieldProps()), createElement("label", Object.assign({}, getLabelProps()), label, help && createElement("span", Object.assign({}, getHelpProps()), help)), prepend, render("select", _extends(_extends({}, inputProps), {}, {
    value: htmlValue,
    onChange: onChange
  }), placeholder && createElement("option", {
    disabled: true,
    value: "",
    key: "placeholder"
  }, placeholder), children), append, error && createElement("span", Object.assign({}, getErrorProps()), error));
}

/**
 * Renders a `<button />` to toggle the state of a boolean value.
 *
 * You can style this element with CSS by selecting the `[aria-checked]`
 * value.
 *
 * In addition to the props listed below, this component accepts
 * all props for an HTML button.
 *
 * #### Example
 *
 * ```jsx
 * <Switch
 *   type="enabled"
 *   label="Show preview"
 *   field={useField(form, "enabled")}
 * />
 * ```
 */

function Switch(props) {
  var _useFieldProps = useFieldProps(props, ["check", "switch"]),
      field = _useFieldProps.field,
      label = _useFieldProps.label,
      help = _useFieldProps.help,
      error = _useFieldProps.error,
      prepend = _useFieldProps.prepend,
      render = _useFieldProps.render,
      children = _useFieldProps.children,
      getFieldProps = _useFieldProps.getFieldProps,
      getLabelProps = _useFieldProps.getLabelProps,
      getErrorProps = _useFieldProps.getErrorProps,
      getHelpProps = _useFieldProps.getHelpProps,
      inputProps = _objectWithoutPropertiesLoose(_useFieldProps, ["field", "label", "help", "error", "append", "prepend", "render", "children", "getFieldProps", "getLabelProps", "getErrorProps", "getHelpProps"]);

  var value = field.value,
      setValue = field.setValue;
  var onClick = useCallback(function () {
    setValue(function (value) {
      return !value;
    });
  }, [setValue]);
  return createElement("div", Object.assign({}, getFieldProps()), prepend, render("button", _extends(_extends({}, inputProps), {}, {
    type: "button",
    role: "switch",
    onClick: onClick,
    "aria-checked": value,
    "aria-label": value ? "On" : "Off"
  }), children), createElement("label", Object.assign({}, getLabelProps()), label, help && createElement("span", Object.assign({}, getHelpProps()), help)), error && createElement("span", Object.assign({}, getErrorProps()), error));
}

/**
 * Renders a `<textarea />`.

 * In addition to the props listed below, this component accepts
 * all props for an HTML input.
 *
 * #### Example
 *
 * ```jsx
 * <TextArea
 *   type="comment"
 *   label="Comment"
 *   field={useField(form, "comment")}
 * />
 * ```
 */

function TextArea(props) {
  var _useFieldProps = useFieldProps(props, ["textarea"]),
      field = _useFieldProps.field,
      label = _useFieldProps.label,
      help = _useFieldProps.help,
      error = _useFieldProps.error,
      append = _useFieldProps.append,
      prepend = _useFieldProps.prepend,
      render = _useFieldProps.render,
      getFieldProps = _useFieldProps.getFieldProps,
      getLabelProps = _useFieldProps.getLabelProps,
      getErrorProps = _useFieldProps.getErrorProps,
      getHelpProps = _useFieldProps.getHelpProps,
      inputProps = _objectWithoutPropertiesLoose(_useFieldProps, ["field", "label", "help", "error", "append", "prepend", "render", "getFieldProps", "getLabelProps", "getErrorProps", "getHelpProps"]);

  var value = field.value,
      setValue = field.setValue;
  var onChange = useCallback(function (event) {
    setValue(event.target.value);
  }, [setValue]);
  return createElement("div", Object.assign({}, getFieldProps()), createElement("label", Object.assign({}, getLabelProps()), label, help && createElement("span", Object.assign({}, getHelpProps()), help)), prepend, render("textarea", _extends(_extends({}, inputProps), {}, {
    value: value,
    onChange: onChange
  })), append, error && createElement("span", Object.assign({}, getErrorProps()), error));
}

function pad(value) {
  return value < 10 ? "0" + value : value.toString();
}

function assert(test, type, value) {
  if (!test) {
    throw new Error("Unable to convert " + JSON.stringify(value) + " to " + type);
  }
}
/**
 * Convert a number to a string
 */


function formatNumber(value) {
  assert(!isNaN(value), "number", value);
  return value.toString();
}
/**
 * Convert a string to number
 */

function parseNumber(value) {
  var number = parseFloat(value);
  if (isNaN(number)) return;
  return number;
}
/**
 * Convert the value from an `input[type=time]` to an ISO-8601 time (UTC).
 */

function parseTime(value, date) {
  if (date === void 0) {
    date = new Date();
  }

  var match = value.match(/^(\d{2}):(\d{2})$/);
  if (!match) return;
  var hours = Number(match[1]);
  if (hours > 23) return;
  var minutes = Number(match[2]);
  if (minutes > 59) return;
  date.setHours(hours);
  date.setMinutes(minutes);
  return pad(date.getUTCHours()) + ":" + pad(date.getUTCMinutes()) + ":00Z";
}
/**
 * Convert an ISO-8601 time (UTC) to the format expected by `input[type=time]`.
 */

function formatTime(value, date) {
  if (date === void 0) {
    date = new Date();
  }

  var match = value.match(/^(\d{2}):(\d{2}):\d{2}Z$/);
  assert(match, "time", value);
  var hours = Number(match[1]);
  assert(hours < 24, "time", value);
  var minutes = Number(match[2]);
  assert(minutes < 60, "time", value);
  date.setUTCHours(hours);
  date.setUTCMinutes(minutes);
  return pad(date.getHours()) + ":" + pad(date.getMinutes());
}
/**
 * Convert the value from an `input[type=datetime-local]` to an ISO-8601 datetime (UTC).
 */

function parseDateTime(value) {
  var date = new Date(value);
  if (isNaN(date.valueOf())) return;
  return date.toISOString();
}
/**
 * Convert an ISO-8601 datetime (UTC) to the format expected by `input[type=datetime-local]`.
 */

function formatDateTime(value) {
  var date = new Date(value);
  assert(!isNaN(date.valueOf()), "datetime", value);
  var year = date.getFullYear();
  var month = date.getMonth() + 1;
  var day = date.getDate();
  var hours = date.getHours();
  var minutes = date.getMinutes();
  return year + "-" + pad(month) + "-" + pad(day) + "T" + pad(hours) + ":" + pad(minutes);
}

export { Checkbox, CheckboxItem, FieldSet, FileInput, FileListInput, Input, Radio, Select, Switch, TextArea, ThemeProvider, concat, createTheme, formatDateTime, formatNumber, formatTime, parseDateTime, parseNumber, parseTime, useTheme };
//# sourceMappingURL=inputs.esm.js.map
